import {
  ArrowsClockwiseIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  CreditCardIcon,
  IdentificationCardIcon,
  InfoIcon,
  MinusIcon,
  PackageIcon,
  PlusIcon,
  PuzzlePieceIcon,
  ReceiptIcon,
  SparkleIcon,
  UsersIcon,
  WarningIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import Drawer from '../../components/Drawer';
import { Badge, Button, Card, Label, Radio, Spinner, Textarea, Select } from 'flowbite-react';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AnimatedModal from '../../components/AnimatedModal';
import StatCard from '../../components/StatCard';
import SimpleTooltip from '../../components/SimpleTooltip';
import {
  useAuth,
  useCancelSubscription,
  useTeamOverview,
  useCancelPendingPlanChange,
  useReactivateSubscription,
  useTrackEvent,
  useAvailableAddons,
  usePurchaseAddon,
  useOpenInvoice,
} from "../../hooks";
import BillingSkeleton from "../../components/SkeletonLoaders/BillingSkeleton";
import { formatDate, formatCurrency } from "../../lib/utils";
import { CurrencyCode } from "../../shared/constants";


const Billing = () => {
  const { t } = useTranslation();
  const { user, subscriptionDetails: details, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: teamOverview, isLoading: isLoadingOverview } = useTeamOverview();
  const { data: availableAddons, isLoading: isLoadingAddons } = useAvailableAddons();
  const isPastDue = details?.subscription_status === "past_due";
  const { data: openInvoice } = useOpenInvoice(isPastDue);
  const cancelMutation = useCancelSubscription();
  const cancelPendingChangeMutation = useCancelPendingPlanChange();
  const reactivateMutation = useReactivateSubscription();
  const purchaseAddonMutation = usePurchaseAddon();
  const { trackEvent } = useTrackEvent();

  const canReconnect =
    details?.cancel_at_period_end === true &&
    (details?.subscription_status === "active" || details?.subscription_status === "trialing");

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCancelPendingModal, setShowCancelPendingModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [cancelImmediate, setCancelImmediate] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [customReason, setCustomReason] = useState("");

  // Billing cost breakdown
  const planAmount = parseFloat(details?.plan?.amount ?? "0");
  const addonMonthlyTotal = (details?.active_addons ?? []).reduce(
    (sum, a) => sum + parseFloat(a.amount) * a.quantity, 0
  );
  const activeAddons = (details?.active_addons ?? []).filter(a => a.quantity > 0);

  // Addon quantity state: addonId -> draft quantity
  const [addonQuantities, setAddonQuantities] = useState<Record<number, number>>({});
  const [stopAddonId, setStopAddonId] = useState<number | null>(null);
  const [stoppingAddonId, setStoppingAddonId] = useState<number | null>(null);
  const [showAddonDrawer, setShowAddonDrawer] = useState(false);
  const [showAddonPaidBanner, setShowAddonPaidBanner] = useState(false);

  useEffect(() => {
    if (searchParams.get("addon_paid") !== "1") return;
    setShowAddonPaidBanner(true);
    refreshUser();
    // Webhook fires ~1-2 s after redirect; re-fetch once more to catch updated limits
    const timer = setTimeout(() => { refreshUser(); }, 2500);
    // Clean up query params from the URL without re-navigating
    navigate({ search: "" }, { replace: true });
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAddonQuantity = (addonId: number, currentQuantity: number) =>
    addonQuantities[addonId] ?? currentQuantity;

  const handleAddonQuantityChange = (addonId: number, currentQuantity: number, delta: number) => {
    const current = getAddonQuantity(addonId, currentQuantity);
    const next = Math.max(0, Math.min(100, current + delta));
    setAddonQuantities((prev) => ({ ...prev, [addonId]: next }));
  };

  const handleSaveAddon = (addonId: number, currentQuantity: number) => {
    const quantity = getAddonQuantity(addonId, currentQuantity);
    trackEvent("button_clicked", { button_id: "save_addon", addon_id: addonId, quantity });
    const base = window.location.origin;
    purchaseAddonMutation.mutate(
      {
        addon_id: addonId,
        quantity,
        success_url: `${base}/billing?addon_paid=1&addon_id=${addonId}&qty=${quantity}`,
        cancel_url: `${base}/billing`,
      },
      {
        onSuccess: (response) => {
          setAddonQuantities((prev) => {
            const next = { ...prev };
            delete next[addonId];
            return next;
          });
          trackEvent("form_submitted", { form_id: "addon_purchase", addon_id: addonId, quantity, result: "success" });
          if (response.payment_url) {
            window.location.href = response.payment_url;
          } else {
            toast.success(response.message || t("billing.addon.saveSuccess", "Add-on updated successfully."));
          }
        },
        onError: (error: Error) => {
          toast.error(error?.message || t("billing.addon.saveError", "Failed to update add-on."));
          trackEvent("error_displayed", { error_context: "addon_purchase", error_message: error?.message ?? "Failed to update add-on" });
        },
      }
    );
  };

  const CANCEL_REASONS = [
    { value: "expensive", label: t("billing.reasons.expensive", "Too expensive") },
    { value: "missing_features", label: t("billing.reasons.missing_features", "Missing key features") },
    { value: "difficult", label: t("billing.reasons.difficult", "Too difficult to use") },
    { value: "alternative", label: t("billing.reasons.alternative", "Found a better alternative") },
    { value: "not_needed", label: t("billing.reasons.not_needed", "No longer need this service") },
    { value: "other", label: t("billing.reasons.other", "Other") },
  ];

  const isLoading = user?.is_team_admin && isLoadingOverview;

  const handleOpenCancelModal = () => {
    setCancelReason("");
    setCustomReason("");
    if (details?.cancel_at_period_end) {
      setCancelImmediate(true);
    } else {
      setCancelImmediate(false);
    }
    setShowCancelModal(true);
    trackEvent("modal_opened", {
      modal_id: "cancel_subscription",
      current_plan: details?.plan?.name ?? null,
    });
  };

  const handleCancelSubscription = async () => {
    const reason = cancelReason === "other" ? customReason : cancelReason;
    trackEvent("button_clicked", {
      button_id: "confirm_cancel_subscription",
      cancel_mode: cancelImmediate ? "immediate" : "end_of_period",
      cancel_reason: reason || null,
    });
    cancelMutation.mutate(
      { immediate: cancelImmediate, reason: reason || undefined },
      {
        onSuccess: (response) => {
          toast.success(
            response.message || t("billing.cancelSuccess", "Subscription updated")
          );
          trackEvent("form_submitted", {
            form_id: "cancel_subscription",
            cancel_mode: cancelImmediate ? "immediate" : "end_of_period",
            result: "success",
          });
          setShowCancelModal(false);
        },
        onError: (error: Error) => {
          toast.error(
            error?.message ||
            t("billing.cancelError", "Failed to cancel subscription")
          );
          trackEvent("error_displayed", {
            error_context: "cancel_subscription",
            error_message: error?.message ?? "Failed to cancel subscription",
          });
        },
      }
    );
  };

  const handleCancelPendingChange = () => {
    trackEvent("button_clicked", {
      button_id: "confirm_cancel_pending_plan_change",
    });
    cancelPendingChangeMutation.mutate(undefined, {
      onSuccess: (res) => {
        toast.success(
          res.message ||
          t("billing.pendingChangeCancelled", "Scheduled plan change cancelled")
        );
        trackEvent("form_submitted", {
          form_id: "cancel_pending_plan_change",
          result: "success",
        });
        setShowCancelPendingModal(false);
      },
      onError: (err: Error) => {
        toast.error(
          err?.message ||
          t("billing.cancelPendingError", "Failed to cancel scheduled change")
        );
      },
    });
  };

  const handleReactivate = () => {
    trackEvent("button_clicked", {
      button_id: "reconnect_subscription",
      plan_name: details?.plan?.name ?? null,
      current_period_end: details?.current_period_end ?? null,
    });
    reactivateMutation.mutate(undefined, {
      onSuccess: (response) => {
        toast.success(response.message || t("billing.reactivateSuccess", "Subscription reactivated successfully"));
        trackEvent("form_submitted", {
          form_id: "reactivate_subscription",
          result: "success",
        });
        setShowReactivateModal(false);
      },
      onError: (error: Error) => {
        toast.error(error?.message || t("billing.reactivateError", "Failed to reactivate subscription"));
        trackEvent("error_displayed", {
          error_context: "reactivate_subscription",
          error_message: error?.message ?? "Failed to reactivate subscription",
        });
      },
    });
  };

  const getStatusBadge = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return <Badge color="success" icon={CheckCircleIcon}>{t('billing.status.active', 'Active')}</Badge>;
      case 'trialing':
        return <Badge color="indigo" icon={SparkleIcon}>{t('billing.status.trialing', 'Trialing')}</Badge>;
      case 'past_due':
        return <Badge color="warning" icon={WarningIcon}>{t('billing.status.pastDue', 'Past Due')}</Badge>;
      case 'canceled':
      case 'unpaid':
        return <Badge color="failure" icon={XCircleIcon}>{t('billing.status.canceled', 'Canceled')}</Badge>;
      default:
        return <Badge color="gray">{t('billing.status.noSubscription', 'No Active Subscription')}</Badge>;
    }
  };

  if (isLoading) return <BillingSkeleton />;

  return (
    <div>
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-2">
        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
          <ReceiptIcon size={24} weight="duotone" />
          <h1 className="text-xl font-bold">
            {t('billing.title', 'Billing & Subscription')}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Button
            color="primary"
            size="md"
            onClick={() => navigate('/billing-history')}
            className="rounded-xl border-gray-100 shadow-sm"
          >
            <ClockIcon size={18} className="mr-2" />
            {t('billing.paymentHistory', 'Payment History')}
          </Button>
        </div>
      </header>

      {user?.is_team_admin && teamOverview && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
          <StatCard
            title={t('billing.teamMembers', 'Team Members')}
            value={teamOverview.total_team_members}
            icon={<UsersIcon size={24} weight="duotone" />}
            accentColor="indigo"
          />
          <StatCard
            title={t('billing.basePlan', 'Base Plan')}
            value={details?.plan ? planAmount : t('billing.noActivePlan', 'No Active Plan')}
            isCurrency={!!details?.plan}
            currency={details?.plan?.currency as CurrencyCode | undefined}
            icon={<PackageIcon size={24} weight="duotone" />}
            accentColor="green"
          />
          <StatCard
            title={t('billing.nextBillingCharge', "Next Month's Charge")}
            value={details?.plan ? planAmount + addonMonthlyTotal : t('billing.noActivePlan', 'No Active Plan')}
            isCurrency={!!details?.plan}
            currency={details?.plan?.currency as CurrencyCode | undefined}
            icon={<CalendarIcon size={24} weight="duotone" />}
            accentColor="amber"
            breakdown={details?.plan && addonMonthlyTotal > 0 ? [
              { label: details.plan.name, value: planAmount },
              { label: t('billing.addon.sectionTitle', 'Add-ons'), value: addonMonthlyTotal },
            ] : undefined}
          />
        </div>
      )}

      {details?.pending_plan && (
        <div className="mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/50 rounded-2xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4 animate-[p-fade-in_0.5s_ease-out]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl text-indigo-600 dark:text-indigo-400">
              <ArrowsClockwiseIcon size={20} weight="duotone" />
            </div>
            <div>
              <p className="text-sm font-bold text-indigo-900 dark:text-indigo-200">
                {t("billing.pendingChange", "Scheduled Plan Change")}
              </p>
              <p className="text-xs text-indigo-700 dark:text-indigo-400 opacity-90 mt-0.5">
                {t(
                  "billing.pendingChangeDesc",
                  "You are scheduled to switch to the {{plan}} plan on {{date}}.",
                  {
                    plan: details.pending_plan.name,
                    date: formatDate(details.pending_plan_at),
                  }
                )}
              </p>
            </div>
          </div>
          <Button
            size="xs"
            color="light"
            onClick={() => setShowCancelPendingModal(true)}
            className="w-full md:w-auto"
          >
            {t("billing.cancelChange", "Cancel Scheduled Change")}
          </Button>
        </div>
      )}

      {isPastDue && openInvoice?.payment_url && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-2xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4 animate-[p-fade-in_0.5s_ease-out]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-xl text-red-600 dark:text-red-400">
              <WarningIcon size={20} weight="duotone" />
            </div>
            <div>
              <p className="text-sm font-bold text-red-900 dark:text-red-200">
                {t("billing.pastDue.bannerTitle", "Payment Required")}
              </p>
              <p className="text-xs text-red-700 dark:text-red-400 opacity-90 mt-0.5">
                {openInvoice.amount_due != null
                  ? t("billing.pastDue.bannerDesc", "A payment of {{amount}} is pending for your add-on.", {
                    amount: formatCurrency(openInvoice.amount_due, (openInvoice.currency ?? "USD") as CurrencyCode),
                  })
                  : t("billing.pastDue.bannerDescGeneric", "A payment is pending on your account.")}
              </p>
            </div>
          </div>
          <a
            href={openInvoice.payment_url}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full md:w-auto"
          >
            <Button size="xs" color="failure" className="w-full md:w-auto">
              {t("billing.pastDue.completePayment", "Complete Payment →")}
            </Button>
          </a>
        </div>
      )}

      {showAddonPaidBanner && (
        <div className="mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 rounded-2xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4 animate-[p-fade-in_0.5s_ease-out]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl text-emerald-600 dark:text-emerald-400">
              <CheckCircleIcon size={20} weight="fill" />
            </div>
            <div>
              <p className="text-sm font-bold text-emerald-900 dark:text-emerald-200">
                {t("billing.addon.paidBannerTitle", "Payment successful!")}
              </p>
              <p className="text-xs text-emerald-700 dark:text-emerald-400 opacity-90 mt-0.5">
                {t("billing.addon.paidBannerDesc", "Your add-on is being activated. Limits will update in a moment.")}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowAddonPaidBanner(false)}
            className="p-1.5 rounded-lg text-emerald-500 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors flex-shrink-0"
            aria-label={t("common.dismiss", "Dismiss")}
          >
            <XCircleIcon size={18} />
          </button>
        </div>
      )}

      <section>
        <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-3 px-2">
          {t('billing.currentPlan', 'Subscription Details')}
        </h2>
        <Card theme={{
          root: {
            children: "flex h-full flex-col p-4 gap-0"
          }
        }}>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-2xl text-indigo-600 dark:text-indigo-400">
                <PackageIcon size={40} weight="duotone" />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    {details?.plan?.name || t('billing.noPlan', 'No Active Plan')}
                  </h3>
                  {details?.plan && getStatusBadge(details.subscription_status)}
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                  {details?.plan
                    ? `${formatCurrency(parseFloat(details.plan.amount), details.plan.currency as CurrencyCode)} / ${details.plan.interval}`
                    : t('billing.freeDescription', 'Basic features included')}
                </p>
              </div>
            </div>

            {!details?.is_in_trial && (
              <div className="flex items-center gap-3 w-full md:w-auto">
                {user?.is_team_admin && (
                  canReconnect ? (
                    <Button color="success" size="sm" onClick={() => setShowReactivateModal(true)} className="flex-1 md:flex-none">
                      <ArrowsClockwiseIcon size={16} className="mr-2" />
                      {t("billing.resumePlan", "Resume Plan")}
                    </Button>
                  ) : (
                    (details?.subscription_status === "active" || details?.subscription_status === "trialing") && (
                      <Button color="light" size="sm" onClick={handleOpenCancelModal} className="flex-1 md:flex-none">
                        {t("billing.cancel", "Cancel")}
                      </Button>
                    )
                  )
                )}
                {details?.plan && details.subscription_status === "active" && (
                  <Button
                    color={activeAddons.length > 0 ? "primary" : "light"}
                    size="sm"
                    onClick={() => setShowAddonDrawer(true)}
                  >
                    <div className="flex items-center gap-1">
                      {activeAddons.length > 0 ? (
                        <SparkleIcon size={15} weight="fill" className="mr-2" />
                      ) : (
                        <PuzzlePieceIcon size={15} weight="duotone" className="mr-2" />
                      )}
                      {t('billing.addon.drawerTrigger', 'Add-ons')}
                      {activeAddons.length > 0 && (
                        <>
                          <span className="mx-2 h-3.5 w-px bg-current opacity-40" aria-hidden />
                          <span className="text-xs font-medium opacity-90 tabular-nums">
                            {t('billing.addon.drawerTriggerActive', '{{count}} active · {{total}}/mo', {
                              count: activeAddons.length,
                              total: formatCurrency(addonMonthlyTotal, (activeAddons[0]?.currency ?? 'USD') as CurrencyCode),
                            })}
                          </span>
                        </>
                      )}
                    </div>
                  </Button>
                )}
                <Button color="primary" onClick={() => navigate('/pricing')} className="flex-1 md:flex-none" size={"sm"}>
                  {details?.plan ? t('billing.changePlan', 'Change Plan') : t('billing.subscribe', 'Subscribe Now')}
                </Button>
              </div>
            )}
          </div>

          {details?.is_in_trial && (
            <div className="mt-4 p-4 bg-indigo-50/50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/50 rounded-xl flex items-center gap-3 text-sm text-indigo-700 dark:text-indigo-300">
              <SparkleIcon size={20} weight="fill" />
              <div>
                <p className="font-semibold">{t('billing.trialActive', 'Free Trial Active')}</p>
                <p className="text-xs opacity-80">
                  {t('billing.trialRemaining', 'Your trial ends in {{days}} days ({{date}}).', {
                    days: details.days_until_trial_end,
                    date: formatDate(details.trial_end),
                  })}
                </p>
                <p className="text-xs opacity-70 mt-1">
                  {t('billing.trialPlanChangeNote', 'To change or cancel your plan during the trial, please raise a support ticket.')}
                </p>
              </div>
            </div>
          )}

          {details?.cancel_at_period_end && (
            <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/50 rounded-xl flex items-center gap-3 text-sm text-amber-700 dark:text-amber-300">
              <InfoIcon size={20} weight="fill" />
              <div>
                <p className="font-semibold">{t('billing.cancellationPending', 'Cancellation Pending')}</p>
                <p className="text-xs opacity-80">
                  {t('billing.cancelDate', 'Subscription will cancel automatically on {{date}}.', {
                    date: formatDate(details.current_period_end),
                  })}
                </p>
              </div>
            </div>
          )}

          {details?.plan && (
            <div className="mt-8 border-t border-gray-100 dark:border-neutral-700 pt-8">
              <h4 className="text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-widest mb-6 px-1 flex items-center gap-2">
                <IdentificationCardIcon size={18} className="text-gray-500 dark:text-gray-400" />
                {t('billing.managementDetails', 'Management Details')}
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-12 px-1">
                <div className="space-y-1.5">
                  <p className="text-[10px] font-extrabold text-gray-500 dark:text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                    <CalendarIcon size={12} className="text-blue-400" />
                    {t('billing.subscriptionSince', 'Subscription Since')}
                  </p>
                  <p className="text-sm font-bold text-gray-700 dark:text-gray-300">
                    {formatDate(details.current_period_start)}
                  </p>
                </div>

                <div className="space-y-1.5">
                  <p className="text-[10px] font-extrabold text-gray-500 dark:text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                    <ClockIcon size={12} className="text-amber-400" />
                    {t('billing.subscriptionAge', 'Subscription Age')}
                  </p>
                  <p className="text-sm font-bold text-gray-700 dark:text-gray-300">
                    {details.subscription_age_days} {t('common.days', 'days')}
                  </p>
                </div>

                <div className="space-y-1.5">
                  <p className="text-[10px] font-extrabold text-gray-500 dark:text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                    <ArrowsClockwiseIcon size={12} className="text-purple-400" />
                    {t('billing.nextPayment', 'Next Payment')}
                  </p>
                  <p className="text-sm font-bold text-gray-700 dark:text-gray-300">
                    {details.next_payment_date ? formatDate(details.next_payment_date) : "—"}
                  </p>
                </div>

                <div className="space-y-1.5">
                  <p className="text-[10px] font-extrabold text-gray-500 dark:text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                    <CreditCardIcon size={12} className="text-rose-400" />
                    {t('billing.billingEmail', 'Billing Email')}
                  </p>
                  <p className="text-sm font-bold text-gray-700 dark:text-gray-300 truncate" title={details.email}>
                    {details.email}
                  </p>
                </div>

                <div className="md:col-span-2 space-y-1.5">
                  <p className="text-[10px] font-extrabold text-gray-500 dark:text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                    <InfoIcon size={12} className="text-teal-400" />
                    {t('billing.servicePeriod', 'Service Period')}
                  </p>
                  <div className="flex items-center gap-3 text-sm font-bold text-gray-700 dark:text-gray-300">
                    <span>{formatDate(details.current_period_start)}</span>
                    <span className="text-gray-300 dark:text-gray-600 font-normal">→</span>
                    <span>{formatDate(details.current_period_end)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Plan Limits */}
          {details?.plan && (
            <div className="mt-8 border-t border-gray-100 dark:border-neutral-700 pt-8">
              <h4 className="text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-widest mb-6 px-1 flex items-center gap-2">
                <PackageIcon size={18} className="text-gray-500 dark:text-gray-400" />
                {t('billing.planLimits', 'Plan Limits')}
                {details.plan?.package?.name && (
                  <span className="ml-auto text-xs font-semibold text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 rounded-full">
                    {details.plan.package.name}
                  </span>
                )}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-12 px-1">
                {([
                  { key: "max_apps" as const, label: t('billing.maxApps', 'Max Apps'), icon: <SparkleIcon size={12} className="text-indigo-400" /> },
                  { key: "max_team_seats" as const, label: t('billing.maxSeats', 'Max Team Seats'), icon: <UsersIcon size={12} className="text-blue-400" /> },
                  { key: "initial_data_pull_days" as const, label: t('billing.dataPullDays', 'Data Pull Days'), icon: <ArrowsClockwiseIcon size={12} className="text-emerald-400" /> },
                  { key: "live_dashboard_window_days" as const, label: t('billing.dashboardWindow', 'Dashboard Window'), icon: <CalendarIcon size={12} className="text-amber-400" /> },
                ] as const).map(({ key, label, icon }) => {
                  const value = details.overrides?.[key] ?? details.plan_limits?.[key] ?? null;
                  const breakdown = key === "max_apps" && details.max_apps_breakdown;
                  return (
                    <div key={key} className="space-y-1.5">
                      <p className="text-[10px] font-extrabold text-gray-500 dark:text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                        {icon}
                        {label}
                      </p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-bold text-gray-700 dark:text-gray-200">
                          {value === -1 ? t("billing.unlimited", "Unlimited") : (value ?? "—")}
                        </p>
                        {breakdown && breakdown.addon_slots > 0 && (
                          <SimpleTooltip
                            content={t("billing.addon.breakdown", "{{base}} from plan + {{addon}} from add-ons = {{total}} total", {
                              base: breakdown.base,
                              addon: breakdown.addon_slots,
                              total: breakdown.total,
                            })}
                          >
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-1.5 py-0.5 rounded-full cursor-default">
                              <InfoIcon size={10} />
                              +{breakdown.addon_slots}
                            </span>
                          </SimpleTooltip>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </Card>
      </section>

      {/* Add-ons Drawer */}
      <Drawer
        show={showAddonDrawer}
        onClose={() => setShowAddonDrawer(false)}
        title=""
        closeButton={false}
        direction="right"
        size="lg"
        className="!w-[520px] max-w-full flex flex-col"
        isMobile={true}
        footer={
          details?.plan ? (
            <div className="w-full">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 text-sm min-w-0">
                  <div className="min-w-0">
                    <p className="text-[10px] font-extrabold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                      {t('billing.addon.footerPlan', 'Plan')}
                    </p>
                    <p className="font-bold text-gray-700 dark:text-gray-200 truncate">
                      {formatCurrency(planAmount, details.plan.currency as CurrencyCode)}
                    </p>
                  </div>
                  {addonMonthlyTotal > 0 && (
                    <>
                      <span className="text-gray-300 dark:text-gray-600 font-light text-lg select-none">+</span>
                      <div className="min-w-0">
                        <p className="text-[10px] font-extrabold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                          {t('billing.addon.sectionTitle', 'Add-ons')}
                        </p>
                        <p className="font-bold text-gray-700 dark:text-gray-200">
                          {formatCurrency(addonMonthlyTotal, (activeAddons[0]?.currency ?? 'USD') as CurrencyCode)}
                        </p>
                      </div>
                      <span className="text-gray-300 dark:text-gray-600 font-light text-lg select-none">=</span>
                    </>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xl font-extrabold text-indigo-600 dark:text-indigo-400 tabular-nums">
                    {formatCurrency(planAmount + addonMonthlyTotal, details.plan.currency as CurrencyCode)}
                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 ml-1">/ mo</span>
                  </p>
                  {details.current_period_end && (
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 flex items-center justify-end gap-1">
                      <CalendarIcon size={11} aria-hidden />
                      {t('billing.addon.footerRenews', 'Renews {{date}}', { date: formatDate(details.current_period_end) })}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : undefined
        }
      >
        {/* ── Custom drawer header ── */}
        <div className="flex items-start justify-between gap-4 pb-4 mb-1 border-b border-gray-100 dark:border-neutral-700">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2.5 bg-indigo-100 dark:bg-indigo-900/40 rounded-xl text-indigo-600 dark:text-indigo-400 flex-shrink-0">
              <PuzzlePieceIcon size={22} weight="duotone" aria-hidden />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-gray-900 dark:text-gray-100 leading-tight">
                {t('billing.addon.drawerTitle', 'Manage Add-ons')}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                {details?.plan?.name ?? ''}
                {details?.current_period_end
                  ? t('billing.addon.drawerSubtitle', ' · Renews {{date}}', { date: formatDate(details.current_period_end) })
                  : ''}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowAddonDrawer(false)}
            className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors flex-shrink-0"
            aria-label={t('common.close', 'Close')}
          >
            <XCircleIcon size={20} aria-hidden />
          </button>
        </div>

        {/* ── Drawer body ── */}
        <div className="space-y-6 pb-2">

          {/* Section A: Active Add-ons */}
          {activeAddons.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full bg-emerald-500 dark:bg-emerald-400 flex-shrink-0" aria-hidden />
                <p className="text-xs font-extrabold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                  {t('billing.addon.activeTitle', 'Active Add-ons')}
                </p>
              </div>
              <div className="mb-3 px-3 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/15 border border-amber-100 dark:border-amber-800/40 flex items-start gap-2">
                <InfoIcon size={14} weight="fill" className="text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" aria-hidden />
                <p className="text-[11px] text-amber-700 dark:text-amber-300 leading-relaxed">
                  {t('billing.addon.activeNote', 'These renew automatically every month. Remove them before your next billing date to avoid charges.')}
                </p>
              </div>
              <div className="rounded-2xl border border-emerald-200 dark:border-emerald-700/40 overflow-hidden divide-y divide-emerald-100 dark:divide-emerald-700/30">
                {activeAddons.map((activeAddon) => {
                  const monthlyTotal = parseFloat(activeAddon.amount) * activeAddon.quantity;
                  return (
                    <div key={activeAddon.id} className="bg-emerald-50/40 dark:bg-emerald-900/10 px-4 py-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{activeAddon.name}</p>
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/40 px-2 py-0.5 rounded-full">
                              ×{activeAddon.quantity} {t('billing.addon.units', 'units')}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">{activeAddon.description}</p>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {formatCurrency(parseFloat(activeAddon.amount), activeAddon.currency as CurrencyCode)}
                              {' '}{t('billing.addon.perUnitPerMonth', '/ unit / mo')}
                            </span>
                            <span className="text-gray-300 dark:text-gray-600" aria-hidden>·</span>
                            <span className="text-sm font-bold text-emerald-700 dark:text-emerald-400">
                              {formatCurrency(monthlyTotal, activeAddon.currency as CurrencyCode)}
                              <span className="text-xs font-normal text-gray-400 dark:text-gray-500 ml-1">{t('billing.addon.perMonth', '/ mo')}</span>
                            </span>
                            {activeAddon.grants_app_slots > 0 && (
                              <>
                                <span className="text-gray-300 dark:text-gray-600" aria-hidden>·</span>
                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-1.5 py-0.5 rounded-full">
                                  +{activeAddon.grants_app_slots * activeAddon.quantity} {t('billing.addon.appSlots', 'app slots')}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => setStopAddonId(activeAddon.id)}
                          className="flex-shrink-0 text-xs font-semibold text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/50 rounded-lg px-2.5 py-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          {t('billing.addon.stopRecurring', 'Stop Recurring')}
                        </button>
                      </div>
                    </div>
                  );
                })}
                <div className="flex items-center justify-between px-4 py-2.5 bg-emerald-100/50 dark:bg-emerald-900/20">
                  <p className="text-xs font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-widest">
                    {t('billing.addon.addonMonthlyTotal', 'Add-on monthly total')}
                  </p>
                  <p className="text-sm font-extrabold text-emerald-700 dark:text-emerald-300 tabular-nums">
                    {formatCurrency(addonMonthlyTotal, (activeAddons[0]?.currency ?? 'USD') as CurrencyCode)}
                    <span className="text-xs font-normal text-emerald-600/70 dark:text-emerald-500 ml-1">/ mo</span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Section B: Available Add-ons */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <p className="text-xs font-extrabold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                {t('billing.addon.availableTitle', 'Available Add-ons')}
              </p>
            </div>

            {isLoadingAddons ? (
              <div className="flex items-center gap-3 px-4 py-6 rounded-2xl border border-gray-100 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800/40">
                <Spinner size="sm" />
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  {t('billing.addon.loading', 'Loading add-ons…')}
                </p>
              </div>
            ) : !availableAddons || availableAddons.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-4 py-8 rounded-2xl border border-dashed border-gray-200 dark:border-neutral-700 text-center">
                <PuzzlePieceIcon size={32} weight="duotone" className="text-gray-300 dark:text-gray-600" aria-hidden />
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  {t('billing.addon.noAddons', 'No add-ons are available for your current plan.')}
                </p>
              </div>
            ) : (
              <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 overflow-hidden divide-y divide-gray-100 dark:divide-neutral-700">
                <div className="px-4 py-2.5 bg-gray-50 dark:bg-neutral-800/60">
                  <p className="text-[11px] text-gray-400 dark:text-gray-500">
                    {t('billing.addon.buySubtitle', 'Charged immediately (prorated). Renews monthly on your plan date.')}
                  </p>
                </div>
                {availableAddons.map((addon) => {
                  const draftQty = getAddonQuantity(addon.id, addon.current_quantity);
                  const isDirty = draftQty !== addon.current_quantity;
                  const isSaving = purchaseAddonMutation.isPending && stoppingAddonId === null;
                  const draftTotal = parseFloat(addon.amount) * draftQty;
                  return (
                    <div key={addon.id} className="px-4 py-4 bg-white dark:bg-neutral-900">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl text-indigo-500 dark:text-indigo-400 flex-shrink-0 mt-0.5">
                          <PuzzlePieceIcon size={16} weight="duotone" aria-hidden />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{addon.name}</p>
                            {addon.current_quantity > 0 && (
                              <span className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-1.5 py-0.5 rounded-full">
                                {t('billing.addon.currentlyActive', 'Currently: {{n}}', { n: addon.current_quantity })}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">{addon.description}</p>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">
                              {formatCurrency(parseFloat(addon.amount), addon.currency as CurrencyCode)}{' '}
                              {t('billing.addon.perUnitPerMonth', '/ unit / mo')}
                            </span>
                            {addon.grants_app_slots > 0 && (
                              <span className="text-[11px] text-gray-400 dark:text-gray-500">
                                · {t('billing.addon.grantsSlots', 'Each unit adds {{n}} app slot(s)', { n: addon.grants_app_slots })}
                              </span>
                            )}
                            {draftQty > 0 && (
                              <>
                                <span className="text-gray-300 dark:text-gray-600" aria-hidden>·</span>
                                <span className="text-xs font-bold text-gray-700 dark:text-gray-200 tabular-nums">
                                  {formatCurrency(draftTotal, addon.currency as CurrencyCode)}
                                  <span className="font-normal text-gray-400 dark:text-gray-500 ml-1">/ mo total</span>
                                </span>
                              </>
                            )}
                          </div>
                          {isDirty && (
                            <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1.5 flex items-center gap-1">
                              <InfoIcon size={11} weight="fill" aria-hidden />
                              {t('billing.addon.proratedNote', 'Charges are prorated for the remainder of your current billing cycle.')}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-3 mt-3">
                        <div className="flex items-center gap-1 bg-gray-50 dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-600 p-1">
                          <button
                            onClick={() => handleAddonQuantityChange(addon.id, addon.current_quantity, -1)}
                            disabled={draftQty <= 0 || isSaving}
                            className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-500 dark:text-gray-300 hover:bg-white dark:hover:bg-neutral-700 hover:shadow-sm disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                            aria-label={t('billing.addon.decrease', 'Decrease quantity')}
                          >
                            <MinusIcon size={13} aria-hidden />
                          </button>
                          <span className="w-9 text-center text-sm font-extrabold text-gray-800 dark:text-gray-100 tabular-nums select-none">
                            {draftQty}
                          </span>
                          <button
                            onClick={() => handleAddonQuantityChange(addon.id, addon.current_quantity, 1)}
                            disabled={draftQty >= 100 || isSaving}
                            className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-500 dark:text-gray-300 hover:bg-white dark:hover:bg-neutral-700 hover:shadow-sm disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                            aria-label={t('billing.addon.increase', 'Increase quantity')}
                          >
                            <PlusIcon size={13} aria-hidden />
                          </button>
                        </div>
                        <Button
                          size="sm"
                          color={isDirty ? 'primary' : 'light'}
                          disabled={!isDirty || isSaving}
                          isProcessing={isSaving && purchaseAddonMutation.variables?.addon_id === addon.id}
                          onClick={() => handleSaveAddon(addon.id, addon.current_quantity)}
                          className="rounded-xl"
                        >
                          {t('billing.addon.save', 'Save')}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Drawer>

      {/* Stop Recurring Add-on Modal */}
      <AnimatedModal
        show={stopAddonId !== null}
        onClose={() => setStopAddonId(null)}
        title={t("billing.addon.stopTitle", "Stop Recurring Add-on")}
        icon={WarningIcon}
        isConfirmation
        confirmText={t("billing.addon.stopConfirm", "Yes, Stop Recurring")}
        confirmButtonColor="red"
        isConfirming={stoppingAddonId !== null && purchaseAddonMutation.isPending}
        onConfirm={() => {
          if (stopAddonId === null) return;
          const id = stopAddonId;
          setStopAddonId(null);
          setStoppingAddonId(id);
          purchaseAddonMutation.mutate(
            { addon_id: id, quantity: 0 },
            {
              onSuccess: (response) => {
                setAddonQuantities((prev) => { const n = { ...prev }; delete n[id]; return n; });
                if (response.payment_url) {
                  window.location.href = response.payment_url;
                } else {
                  toast.success(response.message || t("billing.addon.stopSuccess", "Add-on removed successfully."));
                }
              },
              onError: (error: Error) => {
                toast.error(error?.message || t("billing.addon.saveError", "Failed to update add-on."));
              },
              onSettled: () => setStoppingAddonId(null),
            }
          );
        }}
        description={t(
          "billing.addon.stopWarning",
          "This will set the quantity to 0 and remove this add-on from your next billing cycle. You will keep access until the end of the current period."
        )}
      />

      {/* Reactivation Modal */}
      <AnimatedModal
        show={showReactivateModal}
        onClose={() => setShowReactivateModal(false)}
        title={t("billing.reactivateTitle", "Resume Your Plan")}
        icon={ArrowsClockwiseIcon}
        isConfirmation
        confirmText={t("billing.reactivateConfirm", "Yes, Resume Plan")}
        confirmButtonColor="green"
        isConfirming={reactivateMutation.isPending}
        onConfirm={handleReactivate}

      >
        {t(
          "billing.reactivateDescription",
          "Are you sure you want to resume your {{plan}} plan? Your billing cycle will continue as normal and you will be charged on {{date}}.",
          {
            plan: details?.plan?.name ?? "",
            date: formatDate(details?.current_period_end),
          }
        )}
      </AnimatedModal>

      {/* Cancel Scheduled Change Modal */}
      <AnimatedModal
        show={showCancelPendingModal}
        onClose={() => setShowCancelPendingModal(false)}
        title={t("billing.cancelChangeTitle", "Cancel Scheduled Change")}
        icon={WarningIcon}
        isConfirmation
        confirmText={t("billing.cancelChangeConfirm", "Yes, Cancel It")}
        confirmButtonColor="red"
        isConfirming={cancelPendingChangeMutation.isPending}
        onConfirm={handleCancelPendingChange}
        description={t(
          "billing.cancelChangeWarning",
          "Are you sure you want to cancel the scheduled plan change? Your current plan will remain active."
        )}
      />

      {/* Cancellation Modal */}
      <AnimatedModal
        show={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          trackEvent("modal_closed", { modal_id: "cancel_subscription", dismissed: true });
        }}
        title={t("billing.cancelTitle", "Cancel Subscription")}
        icon={WarningIcon}
        isConfirmation
        confirmText={t("billing.cancelConfirm", "Confirm Cancellation")}
        confirmButtonColor="red"
        confirmationText={t("billing.cancelWord", "CANCEL")}
        isConfirming={cancelMutation.isPending}
        onConfirm={handleCancelSubscription}
        description={t('billing.cancelWarning', 'Are you sure you want to cancel your subscription? This action cannot be undone.')}
      >
        <div className="space-y-4 mt-2">
          <fieldset className="flex flex-col gap-3">
            {!details?.cancel_at_period_end && (
              <div
                className={`flex items-start gap-3 p-3.5 rounded-2xl border-2 transition-all cursor-pointer ${!cancelImmediate ? 'border-indigo-600 dark:border-indigo-500 bg-indigo-50/40 dark:bg-indigo-900/25 shadow-sm' : 'border-gray-200 dark:border-neutral-600 bg-gray-50/50 dark:bg-neutral-700/30 hover:bg-gray-100/60 dark:hover:bg-neutral-700/50'}`}
                onClick={() => setCancelImmediate(false)}
              >
                <div className="mt-1">
                  <Radio
                    id="period-end"
                    name="cancelType"
                    value="false"
                    checked={!cancelImmediate}
                    onChange={() => setCancelImmediate(false)}
                  />
                </div>
                <Label htmlFor="period-end" className="flex flex-col cursor-pointer flex-1">
                  <span className="font-bold text-gray-900 dark:text-gray-100 text-base leading-tight">{t('billing.cancelAtEnd', 'Cancel at end of billing period')}</span>
                  <span className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                    {t('billing.cancelAtEndDesc', "Keep access until the end of your current period. You won't be charged again.")}
                  </span>
                </Label>
              </div>
            )}

            <div
              className={`flex items-start gap-3 p-3.5 rounded-2xl border-2 transition-all cursor-pointer ${cancelImmediate ? 'border-red-600 dark:border-red-500 bg-red-50/40 dark:bg-red-900/20 shadow-sm' : 'border-gray-200 dark:border-neutral-600 bg-gray-50/50 dark:bg-neutral-700/30 hover:bg-gray-100/60 dark:hover:bg-neutral-700/50'}`}
              onClick={() => setCancelImmediate(true)}
            >
              <div className="mt-1">
                <Radio
                  id="immediate"
                  name="cancelType"
                  value="true"
                  checked={cancelImmediate}
                  onChange={() => setCancelImmediate(true)}
                />
              </div>
              <Label htmlFor="immediate" className="flex flex-col cursor-pointer flex-1">
                <span className="font-bold text-red-600 dark:text-red-400 text-base leading-tight">{t('billing.cancelImmediate', 'Cancel immediately')}</span>
                <span className="text-[11px] text-red-400 dark:text-red-300 mt-1 leading-relaxed">
                  {t('billing.cancelImmediateDesc', 'Lose access to all features instantly. A prorated refund for the remaining days will be credited back to your original payment method.')}
                </span>
              </Label>
            </div>
          </fieldset>

          <div className="mt-6 space-y-3">
            <Label htmlFor="cancel-reason" className="text-gray-700 dark:text-gray-300 font-semibold">
              {t("billing.whyLeaving", "Why are you leaving? (Optional)")}
            </Label>
            <Select
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="rounded-xl shadow-sm border-gray-200 dark:border-neutral-600"
            >
              <option value="">{t("billing.selectReason", "Select a reason...")}</option>
              {CANCEL_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </Select>

            {cancelReason === "other" && (
              <div className="animate-[p-fade-in_0.3s_ease-out]">
                <Textarea
                  id="custom-reason"
                  placeholder={t(
                    "billing.customReasonPlaceholder",
                    "Tell us more about how we can improve..."
                  )}
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  rows={3}
                  className="rounded-xl border-gray-200 dark:border-neutral-600 focus:ring-indigo-500 focus:border-indigo-500 dark:focus:ring-indigo-500 dark:focus:border-indigo-500"
                />
              </div>
            )}
          </div>

          {details?.cancel_at_period_end && (
            <div className="p-3.5 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-100/50 dark:border-amber-800/40 flex gap-2.5 items-start">
              <div className="bg-amber-100 dark:bg-amber-900/30 p-1.5 rounded-lg shrink-0">
                <WarningIcon size={14} className="text-amber-600 dark:text-amber-400" weight="fill" />
              </div>
              <div className="flex-1">
                <p className="text-[11px] font-bold text-amber-800 dark:text-amber-300 leading-none mb-1">
                  {t('billing.alreadyPending', 'Cancellation Already Pending')}
                </p>
                <p className="text-[10px] text-amber-700 dark:text-amber-400 leading-relaxed font-medium">
                  {t('billing.alreadyPendingWarning', 'Important: Your subscription is already scheduled for cancellation. This action will terminate your access immediately.')}
                </p>
              </div>
            </div>
          )}
        </div>
      </AnimatedModal>
    </div>
  );
};

export default Billing;
